import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import DatePickerCalendar from './DatePickerCalendar';

interface SingleDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
}

const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date',
  maxDate,
  minDate,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [inputError, setInputError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useClickOutside(containerRef, () => {
    setIsOpen(false);
    setIsEditing(false);
  });

  // Update input value when value changes
  useEffect(() => {
    if (value) {
      setInputValue(formatDisplayDate(value));
    } else {
      setInputValue('');
    }
  }, [value]);

  const handleDateSelect = (selectedDate: Date) => {
    // Apply min/max date constraints
    if (minDate && selectedDate < minDate) return;
    if (maxDate && selectedDate > maxDate) return;

    onChange(selectedDate);
    setIsOpen(false);
    setIsEditing(false);
  };

  const handleClear = () => {
    onChange(null);
    setInputValue('');
    setIsEditing(false);
  };

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '';
    // Return exact DD/MM/YYYY format to match user input
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Auto-format the input as user types
    const formattedValue = formatDateInput(newValue);
    setInputValue(formattedValue);
    setInputError(false); // Clear error when user types

    // Only try to parse when user has finished typing a complete date
    if (formattedValue.length === 10) {
      const parsedDate = parseDateInput(formattedValue);
      if (parsedDate) {
        // Apply min/max date constraints
        if (minDate && parsedDate < minDate) {
          setInputError(true);
          return;
        }
        if (maxDate && parsedDate > maxDate) {
          setInputError(true);
          return;
        }

        // Valid date, update the value but keep editing state
        // User can continue editing if they want
        onChange(parsedDate);
      } else {
        setInputError(true);
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, and navigation keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) {
      return;
    }

    // Allow only digits
    if (!/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const formatDateInput = (input: string): string => {
    // Remove all non-digits
    const digits = input.replace(/\D/g, '');

    // Apply DD/MM/YYYY format
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }
  };

  const parseDateInput = (input: string): Date | null => {
    // Parse DD/MM/YYYY format
    const parts = input.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[2], 10);

      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        // Create date with explicit time to avoid timezone issues
        const date = new Date(year, month, day, 12, 0, 0, 0);

        // Check if the date is valid (e.g., not 31/02/2023)
        if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
          return date;
        }
      }
    }
    return null;
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsEditing(true);
      setIsOpen(false);
      setInputError(false); // Clear error when starting to edit
      // Focus the input after a short delay to ensure the click event is processed
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleInputBlur = () => {
    // Only reset if user has completely finished typing a valid date
    if (inputValue.length === 10) {
      const parsedDate = parseDateInput(inputValue);
      if (parsedDate) {
        // Valid complete date, keep it
        setIsEditing(false);
      } else {
        // Invalid date, reset to current value
        setIsEditing(false);
        if (value) {
          setInputValue(formatDisplayDate(value));
        } else {
          setInputValue('');
        }
      }
    } else if (inputValue.length === 0) {
      // Empty input, reset to current value
      setIsEditing(false);
      if (value) {
        setInputValue(formatDisplayDate(value));
      }
    }
    // If input is incomplete (1-9 characters), keep editing state
    // This allows users to continue typing after entering month
  };

  const handleCalendarClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setIsEditing(false);
    }
  };

  if (disabled) {
    return (
      <div className="w-full flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400">
        <CalendarIcon className="w-5 h-5 mr-2" />
        <span>{value ? formatDisplayDate(value) : placeholder}</span>
      </div>
    );
  }

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className={`flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
          inputError ? 'border-red-500 dark:border-red-400' : 'border-gray-200 dark:border-gray-700'
        }`}
      >
        <div className="flex items-center flex-1">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder="DD/MM/YYYY"
              className="flex-1 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 outline-none"
              maxLength={10}
            />
          ) : (
            <span
              onClick={handleInputClick}
              className={`flex-1 cursor-text ${
                value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {value ? formatDisplayDate(value) : placeholder}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleCalendarClick}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Open calendar"
          >
            <CalendarIcon className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
          </button>

          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              title="Clear date"
            >
              <X className="w-4 h-4 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
            </button>
          )}
        </div>
      </div>

      {inputError && (
        <div className="mt-1 text-xs text-red-500 dark:text-red-400">
          {(() => {
            if (inputValue.length === 10) {
              const parsedDate = parseDateInput(inputValue);
              if (parsedDate) {
                if (maxDate && parsedDate > maxDate) {
                  return 'Future dates are not allowed';
                }
                if (minDate && parsedDate < minDate) {
                  return 'Date is too far in the past';
                }
              }
            }
            return 'Please enter a valid date (DD/MM/YYYY)';
          })()}
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <DatePickerCalendar
            value={{ startDate: value, endDate: null }}
            onSelect={handleDateSelect}
          />
        </div>
      )}
    </div>
  );
};

export default SingleDatePicker;
